/**
 * Lane CAMERA-PITCH: the camera's pitch over its whole range, 0° (ground level) to 90° (straight down), on C0's
 * orthographic camera (docs/research/C0_CAMERA_PITCH.md). No drawing here except through the helpers the renderer
 * and the horizon pass call; tested in Node.
 *
 * - The ground's depth on screen goes with 2·sin(pitch) (`groundK`), floored at 1° so the view never flattens to a line.
 * - Objects (buildings) are seen from their own pitch: the camera's up to 45°, easing to 62° at 90° (an oblique 3/4
 *   view at the top, so walls never vanish). Their heights go with cos(objectPitch)/cos 30° (`heightK`).
 * - Figures are billboards: stretched with `heightK` below 30° as before, as painted from 30° up (`figureK`).
 * - Below `SLICE_PITCH` only a slice of the world round the player is drawn: `FAR` depth units behind, `NEAR` in front;
 *   the far edge is the horizon (passes/pitchHorizon.ts draws the sky above it).
 */
import type { IsoCamera } from './camera';

const DEG = Math.PI / 180;
const COS30 = Math.cos(30 * DEG);

/** The flattest the ground gets (degrees): keeps `unproj` finite at a 0° pitch. */
const GROUND_FLOOR = 1;
/** Objects follow the camera's pitch up to here (degrees), then ease toward `OBJECT_TOP` at 90°. */
const OBJECT_FREE = 45;
const OBJECT_TOP = 62;

export interface PitchFactors {
  groundK: number;
  heightK: number;
  figureK: number;
  viewTan: number;
}

/** The pitch objects are drawn from at a camera pitch `p` (degrees). */
export function objectPitch(p: number): number {
  if (p <= OBJECT_FREE) return p;
  const t = Math.min(1, (p - OBJECT_FREE) / (90 - OBJECT_FREE));
  return OBJECT_FREE + (OBJECT_TOP - OBJECT_FREE) * Math.sin((t * Math.PI) / 2);
}

/** The camera's factors at a pitch `p` (degrees, 0–90); exactly 1 at the painted 30°. */
export function pitchFactors(p: number): PitchFactors {
  if (Math.abs(p - 30) < 1e-9) return { groundK: 1, heightK: 1, figureK: 1, viewTan: Math.tan(30 * DEG) };
  const groundK = 2 * Math.sin(Math.max(GROUND_FLOOR, p) * DEG);
  const heightK = Math.cos(objectPitch(p) * DEG) / COS30;
  const figureK = p < 30 ? heightK : 1;
  return { groundK, heightK, figureK, viewTan: groundK / 2 / (heightK * COS30) };
}

/** Below this pitch (degrees) the world is drawn as a slice with a horizon; from here up, whole. */
export const SLICE_PITCH = 14;
/** The slice at its flattest (`SLICE_FULL` and below): depth units drawn behind and in front of the player. */
const SLICE_FULL = 4;
export const FAR = 80;
export const NEAR = 6;
/** Screen pixels (world picture) of a depth unit at a ground squash of 1. */
const DEPTH_PX = 8;

function smooth(a: number, b: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

/** The slice drawn at a low pitch: depth keys (larger is nearer) and the horizon's row in the world picture. */
export interface PitchBand {
  /** Items whose foot depth is below this are behind the horizon (not drawn). */
  far: number;
  /** Items whose foot depth is above this are behind the camera (not drawn). */
  near: number;
  /** The horizon: world-picture row of the far edge of the ground. */
  horizon: number;
  /** World pixels (screen y, turned and pitched) of the far and near edges. */
  farY: number;
  nearY: number;
  /** 0 at a flat view, 1 where the slice opens out to the whole world (`SLICE_PITCH`). */
  open: number;
  /** Screen pixels a depth unit spans at this pitch. */
  s: number;
}

/**
 * The slice at this frame's pitch, or null when the whole world is drawn. `pd`: the player's depth key; `gy`: the
 * player's ground row (world pixels); `oy`: world pixel → world picture offset; `H`: the world picture's height.
 */
export function pitchBand(iso: IsoCamera, pd: number, gy: number, oy: number, H: number): PitchBand | null {
  if (iso.pitch >= SLICE_PITCH) return null;
  const s = DEPTH_PX * iso.groundK;
  const py = gy + oy;
  const open = smooth(SLICE_FULL, SLICE_PITCH, iso.pitch);
  // Opening out: the cut moves past the screen's edges (with room for tall things standing beyond them).
  const top = (py + 160) / s;
  const bottom = (H - py + 80) / s;
  const far = FAR + Math.max(0, top - FAR) * open;
  const near = NEAR + Math.max(0, bottom - NEAR) * open;
  return { far: pd - far, near: pd + near, horizon: py - far * s, farY: gy - far * s, nearY: gy + near * s, open, s };
}

/** Depth units of ground drawn in front of the player at the flattest (beyond: the near shade, passes/pitchHorizon.ts). */
export const NEAR_GROUND = 160;

/**
 * The world-picture rows the ground is drawn over at a low pitch: from the horizon down to where the drawn ground
 * stops in front of the player (the whole picture's height once the slice opens out). `gy`, `oy`, `H` as pitchBand.
 */
export function pitchGroundRows(band: PitchBand, gy: number, oy: number, H: number): [number, number] {
  const py = gy + oy;
  const bottom = (H - py + 80) / band.s;
  const ground = NEAR_GROUND + Math.max(0, bottom - NEAR_GROUND) * band.open;
  return [Math.max(0, band.horizon - 2), Math.min(H, py + ground * band.s)];
}

/**
 * How far down the player is framed at a low pitch (world-picture pixels): the camera drops toward the ground and
 * the horizon rises in the frame, so more sky shows.
 */
export function pitchDrop(pitch: number, H: number): number {
  return Math.round(H * 0.22 * (1 - smooth(SLICE_FULL, SLICE_PITCH, pitch)));
}

/** Drops the items outside the slice, by their foot's depth key (`depthOf`); in place. */
export function cutToBand<T>(list: T[], band: PitchBand | null, depthOf: (it: T) => number): void {
  if (!band) return;
  let n = 0;
  for (const it of list) {
    const d = depthOf(it);
    if (d >= band.far && d <= band.near) list[n++] = it;
  }
  list.length = n;
}
